import { Directive, inject, Injector, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  NgControl,
  ValidationErrors,
  Validator,
  ValidatorFn,
} from '@angular/forms';
import { startWith, Subject, takeUntil, tap } from 'rxjs';

/**
 * An abstract directive for managing composite form controls in Angular.
 *
 * This directive integrates with Angular's `ControlValueAccessor`, `Validator`, and `OnInit`/`OnDestroy`
 * lifecycle hooks to provide a reusable foundation for complex form structures where child forms are
 * controlled as part of a larger form.
 *
 * ## Purpose:
 * - Allows nested form components to function as part of a composite form.
 * - Ensures proper validation and state propagation between parent and child forms.
 * - Implements `ControlValueAccessor` to enable seamless form binding.
 * - Handles automatic value updates and validation within nested form structures.
 *
 * ## Usage:
 * Extend this abstract class in a form component and define the `form` property as a `FormGroup`
 * or `FormControl`. The directive automatically syncs form values with the Angular form system.
 *
 * ### Example:
 * ```typescript
 * interface ChildForm {
 *  name: string;
 * }
 *
 * @Component({
 *   selector: 'rms-composite-example',
 *   template: `
 *    <form [formGroup]="form">
 *     <input formControlName="name" />
 *    </form>
 *   `
 * })
 * export class ChildForm extends CompositeFormDirective<ChildForm> {
 *   protected form = this.fb.group({
 *     name: '',
 *   });
 * }
 * ```
 *
 * When used inside a reactive form, `CustomInputComponent` will behave as an Angular form control
 * while managing its internal form structure.
 * ### Example:
 * ```html
 *   <rms-composite-example formControlName="example"></rms-composite-example>
 * ```
 */
@Directive()
export abstract class CompositeFormDirective<T> implements ControlValueAccessor, OnInit, OnDestroy, Validator {
  protected readonly fb = inject(FormBuilder);
  protected readonly injector = inject(Injector);
  protected readonly ngControl = inject(NgControl, {
    self: true,
    optional: true,
  });

  protected readonly destroy$ = new Subject<void>();

  protected abstract form: FormGroup | FormArray | FormControl;

  private onTouch?: () => void;
  private onChange?: (value: T) => void;

  private externalValidators: ValidatorFn[] = [];

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    } else {
      this.ngControl = inject(NgControl, { skipSelf: true });
    }
  }

  protected get externalValidationErrors(): ValidationErrors | null {
    return this.ngControl?.control?.errors ?? null;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const internalErrors = this.validateRecursive(this.form) ?? {};

    const externalErrors = this.externalValidators
      .map((fn) => fn(control))
      .filter((err) => err !== null)
      .reduce((acc, curr) => ({ ...acc, ...curr }), {});

    const merged = { ...internalErrors, ...externalErrors };

    return Object.keys(merged).length > 0 ? merged : null;
  }

  ngOnInit(): void {
    const control = this.ngControl?.control;
    if (control?.validator) {
      this.externalValidators = [control.validator];
    }

    control?.setValidators(this.validate.bind(this));
    control?.updateValueAndValidity({ emitEvent: false });

    this.form.valueChanges
      .pipe(
        startWith(this.form.value),
        tap((formValue: T) => {
          this.onTouch?.();
          this.onChange?.(this.onChangeMask(formValue));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    const control = this.ngControl?.control;

    if (control) {
      control.setValidators(this.externalValidators.length ? this.externalValidators : null);
    }
  }

  writeValue(obj: T | null): void {
    if (!obj) {
      return;
    }

    this.form.patchValue(obj);
  }

  registerOnChange(fn: (value: T) => void | undefined): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.form.enabled && this.form.disable() : this.form.disabled && this.form.enable();
  }

  public onChangeMask<X>(value: X | typeof this.form.value): T {
    return value;
  }

  private validateRecursive(control: AbstractControl): ValidationErrors | null {
    if (control instanceof FormGroup || control instanceof FormArray) {
      const controls = Object.values(control.controls);

      return controls.some((c) => this.validateRecursive(c)) ? { invalid: true } : control.errors;
    }

    return control.invalid ? { invalid: true } : null;
  }
}
